import { searchRoot, searchMatches, replaceSearchMatches } from './searchFormatting';
import { matchesAiQualityFilter } from 'libs/aiQuality';
import { convertHtml, getQuoteStyle } from './quoteReplacement';
import { SEGMENT_STATUS } from 'libs/utils';
import _ from 'lodash';
import SegmentsMap from 'libs/segmentsMap';
import ModelSegments from 'models/segments';
import {
	escapeHTML,
	escapeRegExp,
	getMultipleIndexes,
	isNumeric,
} from './commonFuncs';
import { sortSegments, _sortSegments } from './actions';
import { getActiveTaskState, getSegmentContext } from './taskContext';

let searchReplaceLastTerm = '';

// Replacing must not touch locked segments or sections of a split task that belong to other linguists.
const isSegmentEditable = (S) => !(S.isLocked || S.isRevisionLocked || !S.available);

export const allApostrophs = [
	"'",
	"`",
	"ˋ",
	"ˊ",
	"ˈ",
	"ˌ",
	"˘",
	"‘",
	"’",
	"‚",
	"‛",
	"‹",
	"›",
	"“",
	"”",
	"„",
	"‟",
	"′",
	"″",
	"‴",
	"‵",
	"‶",
	"‷",
	"‸",
	"〝",
	"〞",
	"＂",
	"〟",
	"〉",
	"〈",
	"»",
	"«",
	"‧",
	"⁗",
	"⁞",
	"⁝",
	"´",
	"¨",
	"ʼ"
];

class SegmentOperations {
	static initSegments(state, segments) {
		const firstAvailable = segments.find((S) => S.available);

		const arSegments = new SegmentsMap(
			segments.map((S) => [
				S.id,
				{ ...S, checked: false, saving: false, editable: true },
			])
		);

		const arDisplaySegments = state.showAvailableOnly
			? arSegments.filter(({ available }) => available)
			: arSegments;

		const repetitionsCache = {};

		arSegments.forEach((S) => {
			if (S.isInitRepetition || !S.isRepetition) return;

			const arr = !(S.sameAs in repetitionsCache)
				? (repetitionsCache[S.sameAs] = [])
				: repetitionsCache[S.sameAs];

			arr.push(S.id);
		});

		const __state = {
			...state,
			arSegments,
			arDisplaySegments,
			forceRender: new Date(),
			areAllConfirmed: this.areAllConfirmed(arSegments),
			segmentIDToFocus: firstAvailable?.id,
			repetitionsCache,
		};

		return __state;
	}

	static updateTranslationTextRepetitions(
		repetitionsCache,
		arSegments,
		thisSegment
	) {
		return arSegments.withMutations((arSegments) => {
			(repetitionsCache[thisSegment.id] ?? []).forEach((rId) => {
				if (!arSegments.has(rId)) return;

				const repSegment = { ...arSegments.getSegment(rId) };

				//set text and status and save
				repSegment.status = thisSegment.status;
				repSegment.nextStatus = thisSegment.nextStatus;
				repSegment.translationText = thisSegment.translationText;

				if (repSegment.translationText === '') {
					repSegment.matchType = '';
					repSegment.matchScore = '';
				}

				repSegment.forSave = true;

				arSegments.setSegment(rId, repSegment);
			});
		});
	}

	static updateSegmentsChecked(activeSegment, arSegments) {
		return arSegments.map((S) => {
			const isActive = activeSegment?.id == S.id;

			if (isActive && !S.checked) {
				return { ...S, checked: true };
			}

			if (!isActive && S.checked) {
				return { ...S, checked: false };
			}

			return S;
		});
	}

	static setActiveSegment(state, activeSegment) {
		return {
			...state,
			...getActiveTaskState(state, activeSegment),
			activeSegment: activeSegment,
			actSegment: activeSegment,
			arSegments: this.updateSegmentsChecked(activeSegment, state.arSegments),
			arDisplaySegments: this.updateSegmentsChecked(
				activeSegment,
				state.arDisplaySegments
			),
		};
	}

	static copySourceToTargetProcessSegmentsMap(arSegments) {
		return arSegments.map((S) => {
			if (!S.checked) return S;

			return { ...S, translationText: S.sourceText };
		});
	}

	static copySourceToTarget(state) {
		//for all checked
		return {
			...state,
			arSegments: this.copySourceToTargetProcessSegmentsMap(state.arSegments),
			arDisplaySegments: this.copySourceToTargetProcessSegmentsMap(
				state.arDisplaySegments
			),
			forceRender: new Date(),
		};
	}

	static deleteTargetTextProcessSegmentsMap(arSegments) {
		return arSegments.map((S) => {
			if (!S.checked) return S;

			return { ...S, translationText: '' };
		});
	}

	static deleteTargetText(state) {
		//for all checked
		return {
			...state,
			arSegments: this.deleteTargetTextProcessSegmentsMap(state.arSegments),
			arDisplaySegments: this.deleteTargetTextProcessSegmentsMap(
				state.arDisplaySegments
			),
			forceRender: new Date(),
		};
	}

	static setSegmentToSelectionChecked(state, payload) {
		const { segID, checked } = payload;

		//for all checked
		const newState = {
			...state,
			forceRender: new Date(),
		};

		let { arSegments, arDisplaySegments } = newState;

		if (!arSegments.hasSegment(segID)) return newState;
		const _S = arSegments.getSegment(segID);

		arSegments = arSegments.setSegment(segID, { ..._S, checked });

		newState.activeSegment = null;

		if (arDisplaySegments.hasSegment(segID)) {
			arDisplaySegments = arDisplaySegments.setSegment(segID, {
				..._S,
				checked,
			});
		}

		newState.arSegments = arSegments;
		newState.arDisplaySegments = arDisplaySegments;

		return newState;
	}

	static setSegmentCheckedToRange(state, payload) {
		const { segID } = payload;

		const newState = {
			...state,
			forceRender: new Date(),
		};

		let { arSegments, arDisplaySegments } = newState;

		if (!arSegments.hasSegment(segID)) return state;
		const _S = arSegments.getSegment(segID);

		// Range follows the order shown in the grid (sorted/joined view), not editorOrder.
		const task = state.task ?? {};
		const taskOptions =
			task?.options?.[task?.id] ?? task?.options?.[task?.parent] ?? [];
		const displayOrder = {};
		sortSegments(
			arDisplaySegments,
			state.segmentsFilters?.sort,
			[...new Set(taskOptions)]
		).forEach((S, idx) => {
			if (S) displayOrder[S.id] = idx;
		});

		//find 1st segment checked (in displayed order)
		let firstCheckedSegment = null;
		arSegments.forEach((S) => {
			if (!S.checked || displayOrder[S.id] === undefined) return;
			if (
				!firstCheckedSegment ||
				displayOrder[S.id] < displayOrder[firstCheckedSegment.id]
			) {
				firstCheckedSegment = S;
			}
		});

		if (!firstCheckedSegment || displayOrder[segID] === undefined)
			//not found!
			return state;

		const minOrder = displayOrder[segID];
		const maxOrder = displayOrder[firstCheckedSegment.id];

		//no change
		if (minOrder == maxOrder) return state;

		const lo = Math.min(minOrder, maxOrder);
		const hi = Math.max(minOrder, maxOrder);

		const processSegments = (segments) => {
			return segments.map((S) => {
				const _ordr = displayOrder[S.id];

				if (_ordr !== undefined && _ordr >= lo && _ordr <= hi) {
					return {
						...S,
						checked: true
					};
				}

				return {
					...S
				};
			});
		};

		arSegments = processSegments(arSegments);
		arDisplaySegments = processSegments(arDisplaySegments);

		newState.arSegments = arSegments;
		newState.arDisplaySegments = arDisplaySegments;

		//unset focus
		newState.activeSegment = null;

		return newState;
	}

	static selectSegments(state, payload) {
		const newState = {
			...state,
			allSegmentsChecked: payload,
			forceRender: new Date(),
		};

		let { arSegments, arDisplaySegments } = newState;

		const processSegments = (segments) => {
			return segments.map((S) => {
				let checkAction = null;

				if (payload === 'all') {
					checkAction = true;
				}

				if (payload === 'none') {
					checkAction = false;
				}

				if (checkAction == null) {
					if (S.status == payload) {
						checkAction = true;
					} else if (payload === 'locked' && S.isLocked) {
						checkAction = true;
					} else if (payload === 'firstRepetition' && S.isInitRepetition) {
						checkAction = true;
					} else if (
						payload === 'allRepetitions' &&
						(S.isInitRepetition || S.isRepetition)
					) {
						checkAction = true;
					} else {
						checkAction = false;
					}
				}

				if (S.checked !== checkAction) {
					return { ...S, checked: checkAction };
				}

				return S;
			});
		};

		arSegments = processSegments(arSegments);
		arDisplaySegments = processSegments(arDisplaySegments);

		newState.arSegments = arSegments;
		newState.arDisplaySegments = arDisplaySegments;

		return newState;
	}

	static updateRepetitions(newState, newSegment) {
		if (!newSegment.isInitRepetition) return newState;

		const userSettings = window &&
			JSON.parse(window.localStorage.getItem(
				'userPreferences'
			));

		const isAutoPopulateRepetitions = !!userSettings?.autoPopulateRepetitions;

		const processSegments = (segments) => {
			return segments.map((S) => {
				if (S.id == newSegment.id || S.sameAs != newSegment.id || S?.isLocked) return S;

				if (isAutoPopulateRepetitions && S?.status !== 'confirmed') {
					return S;
				}

				return {
					...S,
					status: newSegment.status,
					nextStatus: newSegment.nextStatus,
					forSave: newSegment.forSave,
					translationText: newSegment.translationText,
				};
			});
		};

		newState.arSegments = processSegments(newState.arSegments);
		newState.arDisplaySegments = processSegments(newState.arDisplaySegments);

		return newState;
	}

	static updateSegment(state, payload) {
		let newState = { ...state };
		const {
			skipRepetitions = false,
			skipForceRender = false,
			skipAreAllConfirmed = false,
			...segmentPayload
		} = payload ?? {};

		const sid = segmentPayload?.id;

		if (!newState.arSegments.hasSegment(sid)) return newState;
		const S = newState.arSegments.getSegment(sid);

		const newSegment = {
			...S,
			...segmentPayload,
		};

		newState.arSegments = newState.arSegments.setSegment(sid, newSegment);

		if (newState.arDisplaySegments.hasSegment(sid)) {
			newState.arDisplaySegments = newState.arDisplaySegments.setSegment(sid, {
				...newSegment,
			});
		}

		if (newState?.activeSegment && newState?.activeSegment?.id == sid) {
			newState.activeSegment = { ...newSegment };
		}

		if (!skipRepetitions) {
			newState = this.updateRepetitions(newState, newSegment);
		}

		if (!skipForceRender) {
			newState.forceRender = new Date();
		}

		if (!skipAreAllConfirmed) {
			newState.areAllConfirmed = this.areAllConfirmed(newState?.arSegments);
		}

		return newState;
	}


	static acknowledgeSavedSegments(state, { sent = [], saved = [] }) {
		const sentById = new Map(sent.map((segment) => [String(segment.id), segment]));
		const patches = new Map();
		const cleanText = (text) => text?.replace(/<\/?highlight.*?>/g, '');
		for (const response of saved) {
			if (!response?.id) continue;
			const id = String(response.id);
			const snapshot = sentById.get(id);
			const current = state.arSegments.getSegment(id);
			if (!snapshot || !current || String(current.task) !== String(snapshot.task)
				|| cleanText(current.translationText) !== snapshot.translationText
				|| cleanText(current.sourceText) !== snapshot.sourceText
				|| current.status !== snapshot.status || current.nextStatus !== snapshot.nextStatus) continue;
			// The source ID, task scope and editor repetitions are client identities;
			// a DB response may use a split parent's task ID instead.
			const { task, sourceText, translationText, sameAs, checked, ...fields } = response;
			patches.set(id, { ...current, ...fields, forSave: false, hasChanges: false, updateTM: null });
		}
		if (!patches.size) return state;
		const apply = (segments) => segments.withMutations((map) => {
			patches.forEach((segment, id) => {
				if (map.hasSegment(id)) map.setSegment(id, segment);
			});
		});
		const arSegments = apply(state.arSegments);
		return {
			...state, arSegments, arDisplaySegments: apply(state.arDisplaySegments),
			activeSegment: patches.get(String(state.activeSegment?.id)) ?? state.activeSegment,
			actSegment: patches.get(String(state.actSegment?.id)) ?? state.actSegment,
			areAllConfirmed: this.areAllConfirmed(arSegments), forceRender: new Date(),
		};
	}

	static updateSegments(state, payload) {
		let newState = {
			...state,
		};

		//payload must be array of sements
		payload.forEach((S) => {
			if (!S.id) return;

			const isActiveSegment = S.id === newState?.activeSegment?.id;
			const isQaResultsUpdate =
				Object.keys(S).every((key) => key === 'id' || key === 'qaResults');

			if (isActiveSegment && !isQaResultsUpdate) return;

			const newSeg = {
				...newState.arSegments.getSegment(S.id),
				...S,
				...(isQaResultsUpdate
					? {}
					: {
						forSave: false,
						updateTM: null,
					}),
			};

			newState.arSegments = newState.arSegments.setSegment(S.id, {
				...newSeg,
			});

			if (newState.arDisplaySegments.hasSegment(S.id)) {
				newState.arDisplaySegments = newState.arDisplaySegments.setSegment(
					S.id,
					{
						...newSeg,
					}
				);
			}

			if (newState?.activeSegment && newState?.activeSegment?.id == S.id) {
				newState.activeSegment = { ...newSeg };
			}
		});

		newState.forceRender = new Date();

		newState.areAllConfirmed = this.areAllConfirmed(newState?.arSegments);

		return newState;
	}

	static cleanQaResults(state) {
		const clearResults = (segments) =>
			segments.map((segment) => {
				if (!segment?.id) {
					return segment;
				}

				return {
					...segment,
					qaResults: [],
					forSave: false,
					updateTM: null,
				};
			});

		const arSegments = clearResults(state.arSegments);
		const arDisplaySegments =
			state.arDisplaySegments === state.arSegments
				? arSegments
				: clearResults(state.arDisplaySegments);
		const activeSegment = state.activeSegment?.id
			? { ...arSegments.getSegment(state.activeSegment.id) }
			: state.activeSegment;

		return {
			...state,
			arSegments,
			arDisplaySegments,
			activeSegment,
			actSegment: activeSegment,
			forceRender: new Date(),
		};
	}

	static setLocked() {
		return (dispatch, getState) => {
			const state = getState().traEditor;

			//get selecte segments
			const checkedSegments = state.arSegments
				.filter(({ checked }) => checked)
				.toList()
				.toJS();

			if (checkedSegments.length == 0) return;

			const pairsToLock = checkedSegments.map((s) => ({
				id: s.id,
				isLocked: !s.isLocked,
			}));

			ModelSegments.setLocked({ pairsToLock })
				.then((r) => {
					if (r?.resp) {
						pairsToLock.forEach((P) =>
							dispatch({ type: 'traEditor/updateSegment', payload: P })
						);
						return;
					}
				})
				.catch((err) => {
					console.log(err);
				});
		};
	}

	static setSaving(state, payload) {
		const newState = { ...state };

		const { sid, isSaving } = payload;

		if (!newState.arSegments.hasSegment(sid)) return newState;
		const S = newState.arSegments.getSegment(sid);

		const newSegment = {
			...S,
			saving: isSaving,
			editable: !isSaving,
		};

		newState.arSegments = newState.arSegments.setSegment(sid, newSegment);

		if (newState.arDisplaySegments.hasSegment(sid)) {
			newState.arDisplaySegments = newState.arDisplaySegments.setSegment(sid, {
				...newSegment,
			});
		}

		newState.forceRender = new Date();
		return newState;
	}

	static saveSegment(segment) {
		return (dispatch, getState) => {
			const state = getState().traEditor;

			if (!state.arSegments.hasSegment(segment.id)) return;
			const S = state.arSegments.getSegment(segment.id);

			const { task } = state;

			const pData = {
				segment: {
					...S,
					nextStatus: SEGMENT_STATUS.CONFIRMED,
					targetLang: task.targetLang,
					project: task.project.id,
					file: task.file.id,
					step: task.step,
					task: S.task ?? task.id,
					stepOrdr: task.stepOrdr,
					...getSegmentContext(state, S),
					userName: null,
					userID: null,
				},
			};

			dispatch({
				type: 'traEditor/setSaving',
				payload: { sid: S.id, saving: true },
			});

			ModelSegments.saveSegment(pData)
				.then((r) => {
					if (r) {
						dispatch({ type: 'traEditor/updateSegment', payload: r });
						return;
					}
				})
				.catch((err) => {
					console.log(err);
					//set back segment to editable
				})
				.then((_) => {
					dispatch({
						type: 'traEditor/setSaving',
						payload: { sid: S.id, saving: false },
					});
				});
		};
	}

	static filterAllSegments(state, defSegmentsFilters) {
		const newState = {
			...state,
			segmentsFilters: _.cloneDeep(state.segmentsFilters),
			forceRender: new Date(),
		};

		const initialFilters = _.cloneDeep(defSegmentsFilters);

		const activeFilters = newState.segmentsFilters;
		if (!state.task?.project?.projectSettings?.ai_quality_available?.checked) {
			activeFilters.aiQuality = _.cloneDeep(defSegmentsFilters.aiQuality);
		}

		// Handle unstored (in TMs) segments filter
		let unstoredSegmentsIDs = [];
		if (activeFilters.TMs.unstoredCheckbox) {
			unstoredSegmentsIDs = Object.keys(
				activeFilters?.TMs?.unstoredSegments ?? {}
			);
		}

		let _convertedSource = escapeHTML(activeFilters.text.source);
		let _convertedTarget = escapeHTML(activeFilters.text.target);

		allApostrophs.map(a => {
			_convertedTarget = _convertedTarget.replaceAll(a, '\'');
			_convertedSource = _convertedSource.replaceAll(a, '\'');
		})

		function normalizeText(text) {
			return text.normalize("NFKD").replace(/[\u0300-\u036f]/g, "") // Remove accents
				.replace(/’/g, "'"); // Convert curved apostrophes to straight
		}

		newState.arDisplaySegments = newState.arSegments.filter((segment) => {
			let thisCriteria = true;
			let segmentMatchesCriteria = true;

			const flags = state.caseSensitiveSearch ? 'g' : 'gi';

			// Source text filter
			let regexSrc = state.regexSearch
				? String.raw`${_convertedSource}`
				: escapeRegExp(_convertedSource);
			if (state.wholeWordSearch && _convertedSource)
				regexSrc = String.raw`\b${regexSrc}\b`;
			let regexSource;
			try {
				regexSource = new RegExp(regexSrc, flags);
			} catch (e) {
				return false;
			}
			let sourceWithoutTags = segment.sourceText.replace(/<[^>]+>/gm, '');

			allApostrophs.map(a => {
				sourceWithoutTags = sourceWithoutTags.replaceAll(a, '\'');
			})

			const notNormalizedSource = sourceWithoutTags;
			sourceWithoutTags = normalizeText(sourceWithoutTags);

			// Target text filter
			let regexTrg = state.regexSearch
				? String.raw`${_convertedTarget}`
				: escapeRegExp(_convertedTarget);
			if (state.wholeWordSearch && _convertedTarget)
				regexTrg = String.raw`\b${regexTrg}\b`;
			let regexTarget;
			try {
				regexTarget = new RegExp(regexTrg, flags);
			} catch (e) {
				return false;
			}
			const targetWithoutTags = segment.translationText.replace(
				/<[^>]+>/gm,
				''
			);

			// Remove non-breaking spaces. &nbsp; is not shown in JS, so the JS equivalent "\xa0" is used.
			let targetWithoutBreakingSpace = targetWithoutTags.replaceAll(
				'\xa0',
				' '
			);

			allApostrophs.map(a => {
				targetWithoutBreakingSpace = targetWithoutBreakingSpace.replaceAll(a, '\'');
			})

			const notNormalizedTarget = targetWithoutBreakingSpace;
			targetWithoutBreakingSpace = normalizeText(targetWithoutBreakingSpace);

			thisCriteria = regexSource.test(sourceWithoutTags) || regexSource.test(notNormalizedSource);

			segmentMatchesCriteria =
				segmentMatchesCriteria && thisCriteria && segment?.available;

			thisCriteria = regexTarget.test(targetWithoutBreakingSpace) || regexTarget.test(notNormalizedTarget);
			segmentMatchesCriteria =
				segmentMatchesCriteria && thisCriteria && segment?.available;

			if (activeFilters.TMs.unstoredCheckbox) {
				thisCriteria = unstoredSegmentsIDs.includes(segment?.id);
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.confirmed) {
				thisCriteria = segment.status === SEGMENT_STATUS.CONFIRMED;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.notConfirmed) {
				thisCriteria = segment.status === SEGMENT_STATUS.DRAFT;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.locked) {
				thisCriteria = segment.isLocked === true;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.notLocked) {
				thisCriteria = segment.isLocked === false;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.empty) {
				thisCriteria = segment.translationText === '';
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.notEmpty) {
				thisCriteria = segment.translationText !== '';
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.edited) {
				thisCriteria = activeFilters.editedSegments.includes(segment.id);
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.status.notEdited) {
				thisCriteria = !activeFilters.editedSegments.includes(segment.id);
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.lastInput.manualInput) {
				thisCriteria =
					(segment?.matchType === '' || !segment.matchType) &&
					segment.translationText !== '';
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.lastInput.MT) {
				thisCriteria = segment.matchType === 'MT';
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (!matchesAiQualityFilter(state.aiQualityResults?.[segment.id], segment, activeFilters.aiQuality)) {
				segmentMatchesCriteria = false;
			}

			if (activeFilters.lastInput.fuzzy) {
				const matchScoreInt = parseInt(segment.matchScore);
				thisCriteria =
					segment.matchType === 'TM' &&
					activeFilters.lastInput.fuzzyValues[0] <= matchScoreInt &&
					matchScoreInt <= activeFilters.lastInput.fuzzyValues[1];

				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.sourceText.showRepeatedSegments) {
				thisCriteria = segment.isInitRepetition;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.sourceText.showSecondRepetitions) {
				thisCriteria = segment.sameAs > 0;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			// TODO
			if (activeFilters.sourceText.showRepException) {
				thisCriteria = segment.sameAs < 0;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.sourceText.targetSameAsSource) {
				thisCriteria =
					segment.translationText === segment.sourceText &&
					segment.translationText !== '' &&
					segment.sourceText !== '';
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.sourceText.showNumbersOnly) {
				thisCriteria = isNumeric(segment.sourceText);
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.sourceText.hideNumbersOnly) {
				thisCriteria = !isNumeric(segment.sourceText);
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.tags.withTags) {
				thisCriteria = segment.sourceText.indexOf('xlftag') !== -1;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.tags.withoutTags) {
				thisCriteria = segment.sourceText.indexOf('xlftag') === -1;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.comments.withComments) {
				thisCriteria = segment.commentsCount > 0;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.comments.withoutComments) {
				thisCriteria = segment.commentsCount < 1;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.comments.commentsFilters.length > 0) {
				thisCriteria = activeFilters.comments.commentsFilters.some((ct) => {
					const exists = segment?.commentTypes?.find((taskCT) => ct === taskCT);

					return exists;
				});

				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.comments.resolved) {
				thisCriteria = segment?.resolvedCommentsCount > 0;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.comments.notResolved) {
				thisCriteria =
					segment.commentsCount > 0 &&
					segment?.resolvedCommentsCount < segment.commentsCount;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.errors.withErrors) {
				thisCriteria = segment?.qaResults?.length > 0;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			if (activeFilters.errors.withoutErrors) {
				thisCriteria = !segment.qaResults;
				segmentMatchesCriteria =
					segmentMatchesCriteria && thisCriteria && segment?.available;
			}

			return segmentMatchesCriteria;
		});

		newState.filtersActive =
			JSON.stringify(newState.segmentsFilters) !==
			JSON.stringify(initialFilters);

		return newState;
	}

	static searchAndReplaceDoAction(state, payload) {
		const {
			type,
			replace,
			searchIn,
			actionToDo,
			useRegex,
			caseSensitive,
			wholeWords,
			replaceInSelectedOnly,
		} = payload;

		const removeTagCharacters = (input) => {
			const array = [
				'&lt;',
				'&gt;',
			];

			let newString = input;

			for (const item of array) {
				newString = newString.replaceAll(item, '');
			}

			return newString;
		}

		const htmlUnescape = (input, rotate = false) => {
			let convertSymbols = [
				{
					escaped: '&lt;',
					unescaped: '<',
				},
				{
					escaped: '&gt;',
					unescaped: '>',
				}
			];

			let newString = input;

			for (const item of convertSymbols) {
				if (rotate) {
					newString = newString.replaceAll(item.escaped, item.unescaped);
				} else {
					newString = newString.replaceAll(item.unescaped, item.escaped);
				}
			}

			return newString;
		}

		let search = payload?.search ?? '';
		if (actionToDo === 'createPlaceholder') search = htmlUnescape(search);

		let textToUseAsSearch = useRegex ? search : escapeRegExp(search);
		const regexFlags = 'gm' + (caseSensitive ? '' : 'i');

		if (wholeWords) {
			textToUseAsSearch = `\\b(?:${textToUseAsSearch})\\b`;
		}

		const newState = {
			...state,
			dialogs: {
				...state.dialogs,
				searchAndReplace: { ...state.dialogs.searchAndReplace },
			},
		};

		const setSearchReport = (segmentsFound, instancesFound, error = null) => {
			newState.dialogs.searchAndReplace.data = {
				...newState.dialogs.searchAndReplace.data,
				segmentsFound,
				instancesFound,
				error,
			};
			if (error) {
				newState.dialogs.searchAndReplace.data.navigation = null;
				newState.arDisplaySegments = newState.arDisplaySegments.map(S => {
					const next = { ...S };
					delete next.searchReplaceHighlight;
					return next;
				});
			}
		};

		if (!search) {
			setSearchReport(0, 0);
			return newState;
		}

		const regexPattern = textToUseAsSearch + (actionToDo === 'createPlaceholder' ? '(?![^<]*>|[^<>]*</)' : '');
		let regexToSearch;
		try {
			regexToSearch = new RegExp(regexPattern, regexFlags);
		} catch (e) {
			setSearchReport(0, 0, 'Invalid regular expression');
			return newState;
		}

		let instancesFound = 0;

		const countMatches = (str) => {
			regexToSearch.lastIndex = 0;
			return ((str || '').match(regexToSearch) || []).length;
		};

		const countMatchesSrc = (str, returnMatches = false) => {
			regexToSearch.lastIndex = 0;
			const matches = ((str || '').match(regexToSearch) || []);
			const newMatches = [];
			let matchLength = matches.length;
			for (const item of matches) {
				if (item.match(/<img.*?>/gmi)?.length) {
					matchLength--;
				} else {
					newMatches.push(item);
				}
			}

			if (returnMatches) {
				return newMatches;
			}

			return matchLength;
		};

		const options = { ...payload, search: payload.search ?? '' };
		const isPlaceholder = actionToDo === 'createPlaceholder';
		// Placeholder creation still searches the encoded source markup.
		const matchesFor = (S, target) => isPlaceholder
			? (target ? countMatches(S.translationText) : countMatchesSrc(S.sourceText))
			: searchMatches(searchRoot(target ? S.translationText : S.sourceText), options, target).length;
		const filteredSegmentsForAction = newState.arSegments.filter(S =>
			(searchIn !== 'trg' && matchesFor(S, false) > 0) ||
			(searchIn !== 'src' && matchesFor(S, true) > 0)
		).map(S => {
			instancesFound += (searchIn !== 'trg' ? matchesFor(S, false) : 0) +
				(searchIn !== 'src' ? matchesFor(S, true) : 0);
			return {
				...S,
				isTempLocked: type === 'replace',
				searchReplaceHighlight: {
					...options,
					src: searchIn !== 'trg' ? options.search : '',
					trg: searchIn !== 'src' ? options.search : '',
				},
			};
		});
		setSearchReport(filteredSegmentsForAction.size, instancesFound);

		if (type === 'findHighlight' || (type === 'replace' && actionToDo === 'find')) {
			const queryKey = JSON.stringify([options.search, searchIn, !!useRegex, !!caseSensitive,
				!!wholeWords, payload.targetFormats]);
			const previous = state.dialogs.searchAndReplace.data?.navigation;
			const sort = state.segmentsFilters?.sort;
			const taskOptions = state.task?.options?.[state.task?.parent] ?? state.task?.options?.[state.task?.id];
			const visible = sort && Object.values(sort).some(Boolean)
				? _sortSegments(newState.arDisplaySegments.toList().toArray(), sort)
				: taskOptions
					? [...new Set(taskOptions.map(String))].map(id => newState.arDisplaySegments.getSegment(id)).filter(Boolean)
					: newState.arDisplaySegments.toList().toArray();
			const ids = visible.filter(S => filteredSegmentsForAction.hasSegment(S.id)).map(S => String(S.id));
			const previousIndex = previous?.queryKey === queryKey ? ids.indexOf(previous.segmentId) : -1;
			const segmentId = ids.length ? ids[(previousIndex + 1) % ids.length] : null;
			newState.dialogs.searchAndReplace.data.navigation = { queryKey, segmentId };
			newState.arDisplaySegments = newState.arDisplaySegments.map(S => {
				const next = { ...S };
				delete next.searchReplaceHighlight;
				if (String(S.id) === String(segmentId)) {
					next.searchReplaceHighlight = filteredSegmentsForAction.getSegment(segmentId).searchReplaceHighlight;
				}
				return next;
			});
			if (segmentId !== null) {
				newState.indexToGo = visible.findIndex(S => String(S.id) === segmentId);
				newState.scrollToAlignment = 'center';
			}
		}

		if (type === 'find' || isPlaceholder) {
			newState.arDisplaySegments = filteredSegmentsForAction;
			newState.dialogs.searchAndReplace.data.navigation = null;
		}

		const replaceInSegment = (segment, replaceAllOccurrences = true) => {
			const oldText = (segment.translationText || '').replace(/<\/?highlight.*?>/g, '');
			segment.translationText = replaceSearchMatches(oldText, options, replaceAllOccurrences);
			if (oldText !== segment.translationText) {
				segment.status = SEGMENT_STATUS.DRAFT;
				segment.nextStatus = SEGMENT_STATUS.DRAFT;
				segment.forSave = true;
			}
			return segment;
		};

		const createPlaceholder = (segment) => {
			// If not using regex, just replace with the text input from user
			let placeholder = search;

			let matches = countMatchesSrc(segment.sourceText, true);

			if (useRegex) {
				for (const item of matches) {
					segment.sourceText = segment.sourceText
						.replace(/<\/?highlight.*?>/g, '')
						.replace(
							item,
							`<img src="" class="xlftag" data-type="s" data-tag="p" data-original="${removeTagCharacters(item)}" data-idx="${removeTagCharacters(item)}" />`
						);
					segment.status = SEGMENT_STATUS.DRAFT;
					segment.nextStatus = SEGMENT_STATUS.DRAFT;
					segment.forSave = true;
				}

				return segment;
			}

			// If using regex don't replace with the regex input, but with the actual match
			if (useRegex) {
				const matches = segment.sourceText.match(regexToSearch);
				placeholder = matches[0] ?? '';
			}

			segment.sourceText = segment.sourceText
				.replace(/<\/?highlight.*?>/g, '')
				.replace(
					regexToSearch,
					`<img src="" class="xlftag" data-type="s" data-tag="p" data-original="${placeholder}" data-idx="${placeholder}" />`
				);
			segment.status = SEGMENT_STATUS.DRAFT;
			segment.nextStatus = SEGMENT_STATUS.DRAFT;
			segment.forSave = true;

			return segment;
		};

		//only in target
		if (type == 'replace') {
			let segToReplace;

			if (actionToDo == 'find' && searchIn == 'src') {
				newState.searchReplaceStack = {};
				newState.arDisplaySegments.forEach((S) => {
					const newS = { ...S };
					newS.translationText = newS.translationText.replace(
						/<\/?highlight.*?>/g,
						''
					);
					newState.searchReplaceStack[S.id] = { ...newS };
				});
			}

			if (actionToDo == 'replace') {
				newState.searchReplaceStack = {};
				const currentId = state.dialogs.searchAndReplace.data?.navigation?.segmentId;
				const editableSegments = filteredSegmentsForAction.filter(isSegmentEditable);
				const replSegment = (currentId != null && editableSegments.getSegment(currentId)) || editableSegments
					.sortBy(({ ordr }) => ordr).first(null);

				if (replSegment) {
					segToReplace = { ...newState.arSegments.getSegment(replSegment.id) };
					newState.searchReplaceStack[replSegment.id] = {
						...segToReplace,
						translationText: segToReplace.translationText.replace(
							/<\/?highlight.*?>/g,
							''
						),
					};
					newState.arSegments = newState.arSegments.setSegment(
						replSegment.id,
						replaceInSegment(segToReplace, false)
					);
					if (newState.arDisplaySegments.hasSegment(replSegment.id)) {
						newState.arDisplaySegments = newState.arDisplaySegments.setSegment(
							replSegment.id,
							{ ...newState.arSegments.getSegment(replSegment.id) }
						);
					}
				}
			}

			let replacedSegments = new Map();

			if (actionToDo == 'replaceAll') {
				newState.searchReplaceStack = {};
				filteredSegmentsForAction.forEach((S) => {
					if (!isSegmentEditable(S)) {
						return;
					}

					// When flag enabled, skip non-selected segments
					if (replaceInSelectedOnly && !S?.checked) {
						return;
					}
					
					segToReplace = { ...newState.arSegments.getSegment(S.id) };
					let oldTranslationText = segToReplace?.translationText;
					const replacedSegmentForSave = replaceInSegment(segToReplace);

					if (oldTranslationText !== replacedSegmentForSave?.translationText) {
						newState.searchReplaceStack[S.id] = {
							...newState.arSegments.getSegment(S.id),
							translationText: oldTranslationText.replace(
								/<\/?highlight.*?>/g,
								''
							),
						};
					}

					newState.arSegments = newState.arSegments.setSegment(
						S.id,
						replacedSegmentForSave
					);
					if (newState.arDisplaySegments.hasSegment(S.id)) {
						newState.arDisplaySegments = newState.arDisplaySegments.setSegment(
							S.id,
							{ ...replacedSegmentForSave }
						);
					}

					let replacedSegment = { ...replacedSegmentForSave };

					let repId = parseInt(replacedSegment?.id ?? 0);
					let repTranslationText = replacedSegment?.translationText;

					if (oldTranslationText !== repTranslationText) {
						replacedSegments.set(repId, {
							id: replacedSegment.id,
							ordr: replacedSegment?.ordr,
							oldTranslationText: oldTranslationText,
							newTranslationText: repTranslationText,
							segment: replacedSegment
						})
					}
				});
			}

			if (actionToDo == 'createPlaceholder') {
				newState.searchReplaceStack = {};
				newState.arDisplaySegments.forEach((S) => {
					segToReplace = { ...newState.arSegments.getSegment(S.id) };
					newState.searchReplaceStack[S.id] = { ...segToReplace };
					newState.arSegments = newState.arSegments.setSegment(
						S.id,
						createPlaceholder(segToReplace)
					);
				});
			}

			newState.replacedSegments = replacedSegments;
			if (Object.keys(newState.searchReplaceStack ?? {}).length === 0) {
				newState.searchReplaceStack = null;
			}

			if (
				newState?.activeSegment?.id &&
				newState.arSegments.hasSegment(newState.activeSegment.id)
			) {
				newState.activeSegment = {
					...newState.arSegments.getSegment(newState.activeSegment.id),
				};
			}
		}

		return newState;
	}

	// Converts quote or apostrophe styles in the target text of every editable segment.
	static replaceQuotes(state, payload) {
		const style = getQuoteStyle(payload?.kind, payload?.styleId);
		if (!style) return state;

		const newState = { ...state, lastUpdate: new Date() };
		const searchReplaceStack = {};
		const replacedSegments = new Map();

		state.arSegments.forEach((S) => {
			if (!isSegmentEditable(S)) return;

			const oldText = (S.translationText || '').replace(/<\/?highlight.*?>/g, '');
			if (!oldText) return;

			const { html, count } = convertHtml(oldText, payload.kind, style, payload.fromId);
			if (!count) return;

			const changed = {
				...S,
				translationText: html,
				status: SEGMENT_STATUS.DRAFT,
				nextStatus: SEGMENT_STATUS.DRAFT,
				forSave: true,
			};
			searchReplaceStack[S.id] = { ...S, translationText: oldText };
			newState.arSegments = newState.arSegments.setSegment(S.id, changed);
			if (newState.arDisplaySegments.hasSegment(S.id)) {
				newState.arDisplaySegments = newState.arDisplaySegments.setSegment(S.id, { ...changed });
			}
			replacedSegments.set(parseInt(S.id), {
				id: S.id,
				ordr: S.ordr,
				oldTranslationText: oldText,
				newTranslationText: html,
				segment: changed,
			});
		});

		newState.searchReplaceStack = replacedSegments.size ? searchReplaceStack : null;
		newState.replacedSegments = replacedSegments;

		if (newState?.activeSegment?.id && newState.arSegments.hasSegment(newState.activeSegment.id)) {
			newState.activeSegment = { ...newState.arSegments.getSegment(newState.activeSegment.id) };
		}

		return newState;
	}

	// Narrows the grid to the given segments, like Find & Filter does.
	static showSegments(state, payload) {
		const ids = new Set((payload?.ids ?? []).map(String));

		return {
			...state,
			arDisplaySegments: state.arSegments.filter((S) => ids.has(String(S.id))),
			lastUpdate: new Date(),
		};
	}

	static undoSearchAndReplace(state) {
		const newState = {
			...state,
			lastUpdate: new Date(),
		};

		for (let id in newState.searchReplaceStack) {
			const s = newState.searchReplaceStack[id];
			s.forSave = true;
			s.translationText = s.translationText.replace(/<\/?highlight.*?>/g, '');

			newState.arSegments = newState.arSegments.setSegment(id, { ...s });

			if (newState.arDisplaySegments.hasSegment(id)) {
				newState.arDisplaySegments = newState.arDisplaySegments.setSegment(id, {
					...s,
				});
			}
		}

		newState.searchReplaceStack = null;
		newState.replacedSegments = [];

		return newState;
	}

	static searchAndReplaceClear(state) {
		const newState = {
			...state,
			arDisplaySegments: state.arSegments,
			searchReplaceStack: null,
			replacedSegments: [],
			searchAndReplace: {
				find: '',
				replace: '',
				searchIn: 'trg',
			},
			dialogs: {
				...state.dialogs,
				searchAndReplace: {
					...state.dialogs?.searchAndReplace,
					data: null,
				},
			},
			lastUpdate: new Date(),
		};

		newState.arDisplaySegments = newState.arDisplaySegments.map((S) => {
			if ('searchReplaceHighlight' in S) {
				const newS = { ...S };
				delete newS.searchReplaceHighlight;
				return newS;
			}

			return S;
		});

		return newState;
	}

	static updateQAIngoreState(state, payload) {
		const _newState = {
			...state,
			lastUpdate: +new Date(),
		};

		const { segmentID, type, newState } = payload;
		const seg = {
			..._newState.arSegments.getSegment(segmentID),
		};
		seg.lastUpdate = +new Date();

		seg.qaResults = seg.qaResults.map((E) => {
			if (E.qaTest == type) E.isIgnored = newState;

			return E;
		});

		_newState.arSegments = _newState.arSegments.setSegment(segmentID, seg);

		if (_newState.arDisplaySegments.hasSegment(segmentID)) {
			_newState.arDisplaySegments = _newState.arDisplaySegments.setSegment(
				segmentID,
				{
					...seg,
				}
			);
		}

		return _newState;
	}

	static areAllConfirmed(segments) {
		return segments.every((S) => {
			return (
				S.isLocked || !S.available || S.status === SEGMENT_STATUS.CONFIRMED
			);
		});
	}

	static updateSegmentResolvedComments(state, payload) {
		let newState = { ...state };

		const sid = payload?.id;
		if (!newState.arSegments.hasSegment(sid)) return newState;
		const S = newState.arSegments.getSegment(sid);

		const newSegment = {
			...S,
			...payload,
		};

		newSegment.resolvedCommentsCount = payload.increaseResolvedCount
			? newSegment.resolvedCommentsCount + 1
			: newSegment.resolvedCommentsCount - 1;

		newState.arSegments = newState.arSegments.setSegment(sid, newSegment);

		if (newState.arDisplaySegments.hasSegment(sid)) {
			newState.arDisplaySegments = newState.arDisplaySegments.setSegment(sid, {
				...newSegment,
			});
		}

		if (newState?.activeSegment && newState?.activeSegment?.id == sid) {
			newState.activeSegment = { ...newSegment };
		}

		newState.forceRender = new Date();

		return newState;
	}
}

export default SegmentOperations;
